import { numberWithCommas } from "@fla/common";
import { Button, CheckIcon, CloseIcon, HomeFillIcon, IncDec, SuperSplitIcon } from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { GetStarted_SummaryServiceSchema } from "definitions";
import { useGetStarted } from "hooks";
import { GetStartedStoreTypes } from "interfaces";
import { FC, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: GetStartedStoreTypes;
  onSubmit: SubmitHandler<GetStartedStoreTypes>;
  upgradeToFullService?: () => void;
  startOver?: () => void;
}

const EssentialPackage: FC<IProps> = ({ formId, store, onSubmit, upgradeToFullService }) => {
  const {
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: store,
    resolver: yupResolver(GetStarted_SummaryServiceSchema)
  });

  const { outOfTime } = useGetStarted();

  const totalLegalFees = useMemo(() => {
    let basePrice = 1100;
    if (watch("finance.real_estate_num") > 1) {
      basePrice += 330 * (watch("finance.real_estate_num") - 1);
    }

    if (watch("finance.super_split_num") > 0) {
      basePrice += 440 * watch("finance.super_split_num");
    }

    if (watch("finance.business_nt_num") > 0) {
      basePrice += 330 * watch("finance.business_nt_num");
    }

    if (watch("finance.overseas_assets") === "yes") {
      basePrice += 440;
    }

    if (outOfTime(store)) {
      basePrice += 440;
    }

    return basePrice;
  }, [watch(), outOfTime(store)]);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(v => onSubmit({ ...v, legal_fees: totalLegalFees, package: "essential" }))}
      className="space-y-10"
    >
      <div className="mb-8 pb-8 py-10 border-b space-y-2 bg-secondary text-white rounded-t-lg">
        <p className="text-center">YOU’RE SUITABLE FOR OUR</p>
        <h2 className="text-center">Essentials Package</h2>
      </div>

      <section className="space-y-5">
        <ul className="space-y-5 px-5 md:px-10">
          {/* Consultation  */}
          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">Consultation with lawyer (basic)</p>
              <p className="text-sm text-gray-600 font-light">
                A 30 minute consultation with a family lawyer to obtain further details needed to prepare the documents.
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">We prepare the court documents</p>
              <p className="text-sm text-gray-500 font-light">
                This includes drafting the Consent Orders Application and the minute
              </p>
            </div>
          </li>

          <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
            <div className="w-8">
              <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" />
            </div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">We will lodge it with the court on your behalf</p>
              <p className="text-sm text-gray-500 font-light">
                You don't need to worry about the lodgement complexities. You just need to return the signed documents
                to us.
              </p>
            </div>
          </li>
        </ul>
        <div className="px-5 md:px-10">
          <p className="font-bold">Your Combined Assets</p>
          <p className="text-sm text-gray-600 font-light">
            Please specify the assets you or your partner own, even if it's not being divided.
          </p>
        </div>
        <div className="space-y-5 px-5 md:px-10">
          {watch("finance.real_estate") === "no" ? (
            <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CloseIcon classes="w-7 text-red-500" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">No Real Estate </p>
                <p className="text-sm text-gray-500 font-light">You have stated neither of you have any real estate.</p>
              </div>
            </div>
          ) : watch("finance.real_estate") === "yes" ? (
            <IncDec
              register={register}
              watch={watch}
              name="finance.real_estate_num"
              id="finance.real_estate_num"
              title="How many properties do you both own?"
              subTitle="Include all real estate owned by you and your partner, even if it’s not being divided."
              setValue={setValue}
              error={errors?.finance?.real_estate_num?.message ?? ""}
              icon={HomeFillIcon}
            />
          ) : null}

          {/* Super Split */}
          {watch("finance.super_split") === "no" ? (
            <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
              <div className="w-8">
                <CloseIcon classes="w-7 text-red-500" />
              </div>
              <div className="w-[calc(100%_-_2rem] pl-5">
                <p className="font-bold">No Super Split Required </p>
                <p className="text-sm text-gray-500 font-light">
                  You do not intend to transfer any superannuation between your super funds.
                </p>
              </div>
            </div>
          ) : watch("finance.super_split") === "yes" ? (
            <IncDec
              register={register}
              watch={watch}
              name="finance.super_split_num"
              id="finance.super_split_num"
              title="How many super splits are needed?"
              subTitle="You only need a super split if you’re transferring super from one person’s super account to the person’s super account."
              setValue={setValue}
              error={errors?.finance?.super_split_num?.message}
              icon={SuperSplitIcon}
            />
          ) : null}

          {/* Business No Trust  */}
          {watch("finance.business") === "yes" ? (
            <IncDec
              register={register}
              watch={watch}
              name="finance.business_nt_num"
              id="finance.business_nt_num"
              title="How many businesses (without a trust) do you both own?"
              subTitle="Include it, even if you’re not dividing it."
              setValue={setValue}
              error={errors?.finance?.business_nt_num?.message}
            />
          ) : null}

          {watch("finance.business_trust") === "yes" ? (
            <IncDec
              register={register}
              watch={watch}
              name="finance.business_trust_num"
              id="finance.business_trust_num"
              title="How many businesses (linked to a trust) are there?"
              subTitle="Eg ABC Pty Ltd as trustee for the Smith Family Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
              setValue={setValue}
              error={errors?.finance?.business_trust_num?.message}
            />
          ) : null}

          {watch("finance.other_trust") === "yes" ? (
            <IncDec
              register={register}
              watch={watch}
              name="finance.trust_num"
              id="finance.trust_num"
              title="How many (non-business) trusts do you both have an interest in?"
              subTitle="Eg: Family Trust and Unit Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
              setValue={setValue}
              error={errors?.finance?.trust_num?.message}
            />
          ) : null}
        </div>

        {outOfTime(store) ? (
          <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px] mx-10">
            <div className="w-8">{/* <CheckIcon strokeColor="text-green-600 fill-current w-7 h-auto" /> */}</div>
            <div className="w-[calc(100%_-_2rem] pl-5">
              <p className="font-bold">You're Out Of Time (extra charge) </p>
              <p className="text-sm text-gray-500 font-light">
                The deadline to lodge consent orders has expired. The good news is you can still apply but we'll need to
                conduct additional work to get it through. Extra charge of $440.{" "}
              </p>
            </div>
          </div>
        ) : null}

        <section className="w-full bg-[#f2f2f2] p-5 md:p-10 space-y-5">
          <div className="pb-8">
            <div className="pb-5 flex flex-col justify-center items-center space-y-3">
              <p className="font-bold">Legal fees for the above work</p>
              <p className="font-bold text-2xl">${numberWithCommas(totalLegalFees)}</p>
              <p className="text-xs text-gray-600">including GST</p>
            </div>

            <div className="bg-gray-200/80 p-10 rounded-[10px]">
              <h5 className="text-center">Not included in the essentials package</h5>
              <ul className="space-y-5 my-8 mx-auto">
                <li className="flex items-center space-x-4 bg-white p-3 rounded-[10px]">
                  <div className="w-8 flex justify-center">
                    <CloseIcon strokeColor="text-red-500" classes="w-7" />
                  </div>
                  <div className="w-[calc(100%_-_2rem)]">
                    <p className="text-sm">
                      If the court rejects your agreement, or has any queries (called requisition), this will incur
                      extra fees in the Essential package.
                    </p>
                  </div>
                </li>

                <li className="flex items-center space-x-4 bg-white p-3 rounded-[10px]">
                  <div className="w-8 flex justify-center">
                    <CloseIcon strokeColor="text-red-500" classes="w-7" />
                  </div>
                  <div className="w-[calc(100%_-_2rem)]">
                    <p className="text-sm">Excludes communication with your partner</p>
                  </div>
                </li>
                <li className="flex items-center space-x-4 bg-white p-3 rounded-[10px]">
                  <div className="w-8 flex justify-center">
                    <CloseIcon strokeColor="text-red-500" classes="w-7" />
                  </div>
                  <div className="w-[calc(100%_-_2rem)]">
                    <p className="text-sm">
                      Excludes matters with combined net assets over $1.5 million (eg assets minus debts)
                    </p>
                  </div>
                </li>

                <li className="flex items-center space-x-4 bg-white p-3 rounded-[10px]">
                  <div className="w-8 flex justify-center">
                    <CloseIcon strokeColor="text-red-500" classes="w-7" />
                  </div>
                  <div className="w-[calc(100%_-_2rem)]">
                    <p className="text-sm">
                      Excludes complex situations (eg if there is a family trust, unit trust, SMSF, overseas pension),
                      even if those assets are not being divided
                    </p>
                  </div>
                </li>
              </ul>

              <div className="flex flex-col items-center my-5 space-y-5 max-w-xs md:max-w-sm mx-auto">
                <Button
                  fontSize="lg"
                  borderRadius="xl"
                  form="onboarding-form"
                  className={classNames(
                    "w-fit font-semibold px-10 py-2 flex justify-center items-center bg-primary/20 !text-primary hover:bg-primary hover:!text-white transition-all"
                  )}
                  onClick={upgradeToFullService}
                  type="button"
                >
                  Upgrade to Full Service
                </Button>
              </div>
              <p className="text-sm text-justify">
                Our team handles everything. Full Services includes detailed legal advice and assistance for the
                duration of your matter, including whether the split looks fair, communication with your former partner
                and their lawyer, and dealing with the Court if there is a requisition (at no extra cost).
              </p>
            </div>
          </div>
        </section>
      </section>
    </form>
  );
};

export default EssentialPackage;
